import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Lang = 'en' | 'fa';
export type Dir = 'ltr' | 'rtl';
export type Theme = 'daylight' | 'midnight' | 'system';
export type Density = 'comfortable' | 'compact';
export type Numerals = 'latn' | 'arabext';

interface UiState {
  lang: Lang;
  dir: Dir;
  theme: Theme;
  density: Density;
  numerals: Numerals;
  sidebarCollapsed: boolean;
  /** AppShell-mounted right rail: open vs collapsed strip. Persists across pages. */
  splitPaneOpen: boolean;
  /** Width in px when open. Clamped on write to [SPLIT_PANE_MIN_PX, SPLIT_PANE_MAX_PX]. */
  splitPaneWidth: number;
  /** Active section id in the rail. Pages register their own; AppShell falls back
   *  to the first registered section when this id isn't present on the active page. */
  splitPaneActiveSectionId: string | undefined;
  /** Per-page selection-driven SplitPane widths, keyed by stable string. */
  paneWidths: Record<string, number>;
  setLang: (lang: Lang) => void;
  setTheme: (theme: Theme) => void;
  setDensity: (d: Density) => void;
  setNumerals: (n: Numerals) => void;
  toggleTheme: () => void;
  toggleDensity: () => void;
  toggleSidebar: () => void;
  toggleSplitPane: () => void;
  setSplitPaneWidth: (px: number) => void;
  setSplitPaneActiveSectionId: (id: string | undefined) => void;
  /** Convenience: set active section + ensure the pane is open. Pages call this
   *  on selection ("auto-open the Detail tab"). */
  openSplitPaneToSection: (id: string) => void;
  setPaneWidth: (key: string, px: number) => void;
}

export const SPLIT_PANE_MIN_PX = 280;
export const SPLIT_PANE_MAX_PX = 640;
export const SPLIT_PANE_DEFAULT_PX = 380;

function dirForLang(lang: Lang): Dir {
  return lang === 'fa' ? 'rtl' : 'ltr';
}

function defaultNumeralsFor(lang: Lang): Numerals {
  return lang === 'fa' ? 'arabext' : 'latn';
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      lang: 'en',
      dir: 'ltr',
      // New default: the art-deco teal + Persian gold midnight theme.
      // Existing users keep whatever's in their persisted Zustand slot;
      // only first-time visitors see the new default.
      theme: 'midnight',
      density: 'comfortable',
      numerals: 'latn',
      // Collapsed by default — gives the work surface more room out of
       // the box; hovering the rail auto-expands it (handled in AppShell)
       // for quick navigation, and the toggle button persists the
       // analyst's preferred state across sessions.
      sidebarCollapsed: true,
      splitPaneOpen: true,
      splitPaneWidth: SPLIT_PANE_DEFAULT_PX,
      splitPaneActiveSectionId: undefined,
      paneWidths: {},

      setLang: (lang) =>
        set({
          lang,
          dir: dirForLang(lang),
          numerals: defaultNumeralsFor(lang),
        }),

      setTheme: (theme) => set({ theme }),
      setDensity: (density) => set({ density }),
      setNumerals: (numerals) => set({ numerals }),

      toggleTheme: () => {
        const current = get().theme;
        const next: Theme = current === 'midnight' ? 'daylight' : 'midnight';
        set({ theme: next });
      },

      toggleDensity: () => {
        const current = get().density;
        set({ density: current === 'compact' ? 'comfortable' : 'compact' });
      },

      toggleSidebar: () => set({ sidebarCollapsed: !get().sidebarCollapsed }),

      toggleSplitPane: () => set({ splitPaneOpen: !get().splitPaneOpen }),

      setSplitPaneWidth: (px) =>
        set({
          splitPaneWidth: Math.round(
            Math.max(SPLIT_PANE_MIN_PX, Math.min(SPLIT_PANE_MAX_PX, px)),
          ),
        }),

      setSplitPaneActiveSectionId: (id) =>
        set({ splitPaneActiveSectionId: id }),

      openSplitPaneToSection: (id) =>
        set({ splitPaneOpen: true, splitPaneActiveSectionId: id }),

      setPaneWidth: (key, px) =>
        set({
          paneWidths: {
            ...get().paneWidths,
            [key]: Math.round(
              Math.max(SPLIT_PANE_MIN_PX, Math.min(SPLIT_PANE_MAX_PX, px)),
            ),
          },
        }),
    }),
    {
      name: 'jam-ui-prefs',
      version: 2,
      // v1 → v2: two unrelated forward-renames landed in the same bump.
      //  - `parchment` theme → `daylight` (light theme moved from warm-cream
      //    to neutral-grey).
      //  - `evidencePaneOpen`/`evidencePaneWidth` → `splitPane*` (the rail
      //    was lifted from command-center into AppShell).
      migrate: (persisted, fromVersion) => {
        if (fromVersion >= 2 || !persisted || typeof persisted !== 'object') {
          return persisted as UiState;
        }
        // Destructure the renamed keys out of `rest` so the persisted v2
        // payload stops carrying `theme: 'parchment'` /
        // `evidencePane{Open,Width}` forward forever.
        const {
          theme,
          evidencePaneOpen,
          evidencePaneWidth,
          ...rest
        } = persisted as Omit<Partial<UiState>, 'theme'> & {
          theme?: string;
          evidencePaneOpen?: boolean;
          evidencePaneWidth?: number;
        };
        return {
          ...rest,
          theme: theme === 'parchment' ? 'daylight' : theme,
          splitPaneOpen: evidencePaneOpen ?? true,
          splitPaneWidth: evidencePaneWidth ?? SPLIT_PANE_DEFAULT_PX,
          splitPaneActiveSectionId: undefined,
        } as UiState;
      },
    },
  ),
);

/** Apply UI store state to <html> attrs + class. Idempotent. */
export function applyUiToDom(state: UiState) {
  const html = document.documentElement;
  html.setAttribute('lang', state.lang);
  html.setAttribute('dir', state.dir);
  html.setAttribute('data-density', state.density);

  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const useMidnight =
    state.theme === 'midnight' || (state.theme === 'system' && prefersDark);

  html.classList.toggle('midnight', useMidnight);
  html.classList.toggle('daylight', !useMidnight);
}

// `index.html` contains an inline pre-hydrate script that reads
// `jam-ui-prefs` from localStorage and writes lang/dir/theme/density to
// <html> BEFORE any React bundle loads — so there's no LTR→RTL flash on
// first paint. Once Zustand finishes its own async hydration we simply
// keep the DOM in sync with any runtime changes (theme toggle, lang
// switch, etc).
if (typeof document !== 'undefined') {
  useUiStore.persist.onFinishHydration((state) => {
    applyUiToDom(state);
  });
  useUiStore.subscribe((state) => {
    applyUiToDom(state);
  });
}
